<template>
  <div
    :class="[
      showCounterpartyType
        ? 'grid-cols-[max-content_auto_max-content_max-content_max-content_max-content_160px]'
        : 'grid-cols-[max-content_auto_max-content_max-content_max-content_160px]',
      table(),
    ]"
  >
    <div :class="tableHeader()">
      <div class="col-start-2">{{ t('description') }}</div>
      <div v-if="showCounterpartyType">{{ t('counterpartyType.label') }}</div>
      <div>{{ t('dueDate') }}</div>
      <div :class="tableCell({ numeric: true })">{{ t('amount') }}</div>
      <div :class="tableCell({ numeric: true })">{{ t('open') }}</div>
      <div :class="tableCell({ numeric: true })">{{ t('allocate') }}</div>
    </div>
    <template v-if="ledgerEntries.length">
      <label
        v-for="entry in ledgerEntries"
        :key="entry.id"
        :class="tableRow({ interactive: true, selected: isSelected(entry.id) })"
        data-testid="ledger-entry-selector-item"
      >
        <Checkbox
          :model-value="isSelected(entry.id)"
          :disabled="disabled"
          @update:model-value="toggleEntry(entry.id)"
        />
        <LedgerEntryListItemTitle :entry="entry" />
        <CounterpartyTypeBadge
          v-if="showCounterpartyType"
          :counterparty-type="entry.counterpartyType"
        />
        <div>
          {{ entry.dueDate ? formatDate(entry.dueDate, dateFormat) : '-' }}
        </div>
        <div :class="tableCell({ numeric: true, class: getAmountTextColor(entry.amount) })">
          {{ entry.amount > 0 ? '+' : '' }}{{ formatCurrency(entry.amount / 100) }}
        </div>
        <div
          :class="[
            tableCell({
              numeric: true,
              class: [getAmountTextColor(entry[amountToSelectKey]), 'font-bold'],
            }),
          ]"
        >
          <span v-currency="entry[amountToSelectKey] / 100" />
        </div>
        <div :class="tableCell({ numeric: true, class: 'flex justify-end' })" @click.stop>
          <Input
            v-if="
              (amountToSelectKey === 'amount' ||
                $can(
                  'create',
                  ['amountToPay', 'amountToRequestPay'].includes(amountToSelectKey)
                    ? 'PaymentAllocation'
                    : 'InvoiceAllocation',
                  'amount',
                )) &&
              isSelected(entry.id)
            "
            :model-value="getCustomAmount(entry)"
            type="number"
            :min-fraction-digits="2"
            :max-fraction-digits="2"
            :min="getMinAmount(entry)"
            :max="getMaxAmount(entry)"
            :step="0.01"
            adornment="€"
            class="w-full"
            @update:model-value="(v) => updateCustomAmount(entry, v)"
          />
          <span
            v-else-if="isSelected(entry.id)"
            :class="[getAmountTextColor(getDefaultAmount(entry)), 'font-bold']"
            >{{ formatCurrency(getDefaultAmount(entry) / 100) }}</span
          >
          <span v-else class="text-obsidian-400">–</span>
        </div>
      </label>
    </template>
    <div v-if="ledgerEntries.length && modelValue.length > 0" :class="tableFooter({ muted: true })">
      <div :class="showCounterpartyType ? 'col-span-6' : 'col-span-5'">
        {{ t('totalSelected') }}
      </div>
      <div
        :class="[
          tableCell({
            numeric: true,
            class: [getAmountTextColor(selectedAmountToAllocateSum), 'font-bold'],
          }),
        ]"
      >
        <span v-currency="selectedAmountToAllocateSum / 100" />
      </div>
    </div>
    <EntityListExceptions
      :status="status"
      :error="error"
      :items-count="ledgerEntries.length"
      :no-items-text="t('noLedgerEntries')"
      class="col-span-full"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatCurrency } from '@/utils/format-numbers.ts';
import { useLedgerEntries } from '@/queries/use-ledger.ts';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data.ts';
import { FindAllLedgerEntriesSort, Order } from '@carvia/ros-client-types';
import EntityListExceptions from '../EntityListExceptions.vue';
import { formatDate } from '@/hooks/use-formatted-date.ts';
import { useDateFormat } from '@/hooks/use-date-format.ts';
import Checkbox from '../Checkbox.vue';
import Input from '../Input.vue';
import type { CounterpartyType, LedgerEntry } from '@/entities/ledger/ledger-entry.entity.ts';
import LedgerEntryListItemTitle from './LedgerEntryListItemTitle.vue';
import CounterpartyTypeBadge from '@/components/badges/CounterpartyTypeBadge.vue';
import {
  table,
  tableCell,
  tableFooter,
  tableHeader,
  tableRow,
} from '../../../../theme/lib/cva/grid.ts';
import { getAmountTextColor } from './utils/get-amount-text-color.ts';
import { useAbility } from '@casl/vue';
import { isLedgerEntryEligibleForInvoicing } from '@/utils/is-ledger-entry-eligible-for-invoicing';

export type SelectedLedgerEntry = { id: string; customAmount?: number };

type BookingScope = {
  bookingId: string;
  invoiceId?: never;
  counterpartyType?: CounterpartyType | null;
  includeAllCounterparties?: boolean;
};
type InvoiceScope = {
  invoiceId: string;
  bookingId?: never;
};
type Scope = BookingScope | InvoiceScope;

const props = defineProps<{
  modelValue: SelectedLedgerEntry[];
  scope: Scope;
  allowAllocationOutsideOfBounds?: boolean;
  disabled?: boolean;
  amountToSelectKey: 'amount' | 'amountToPay' | 'amountToInvoice' | 'amountToRequestPay';
  includeEntirelyAllocated?: boolean;
  customFilter?: (value: LedgerEntry, index?: number, array?: LedgerEntry[]) => boolean;
  groupByLane?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: SelectedLedgerEntry[]): void;
  (e: 'updateTotalAmount', amount: number): void;
}>();

const isBookingScope = (scope: Scope): scope is BookingScope => scope.bookingId !== undefined;

const showCounterpartyType = computed(
  () => isBookingScope(props.scope) && !!props.scope.includeAllCounterparties,
);

const dateFormat = useDateFormat();
const { t } = useI18n();
const { can } = useAbility();

const {
  data: ledgerEntriesData,
  status,
  error,
} = useLedgerEntries(
  computed(() => ({
    bookingIds: isBookingScope(props.scope) ? [props.scope.bookingId] : undefined,
    counterpartyTypes:
      isBookingScope(props.scope) &&
      props.scope.counterpartyType &&
      !props.scope.includeAllCounterparties
        ? [props.scope.counterpartyType]
        : undefined,
    invoiceIds: !isBookingScope(props.scope) ? [props.scope.invoiceId] : undefined,
    sort: [FindAllLedgerEntriesSort.DUE_DATE],
    order: Order.ASC,
    limit: 999,
  })),
);

const defaultFilter = computed(() => {
  if (
    !props.includeEntirelyAllocated &&
    ['amountToPay', 'amountToRequestPay'].includes(props.amountToSelectKey)
  ) {
    return (ledgerEntry: LedgerEntry) =>
      ledgerEntry[props.amountToSelectKey] !== 0 || ledgerEntry.paymentAllocations.length === 0;
  }
  if (!props.includeEntirelyAllocated && props.amountToSelectKey === 'amountToInvoice') {
    return isLedgerEntryEligibleForInvoicing;
  }
  return Boolean;
});

const ledgerEntriesDataFlat = useFlattenPaginatedData(ledgerEntriesData);
const ledgerEntries = computed(() => {
  const entries = (ledgerEntriesDataFlat.value ?? []).filter(
    props.customFilter ?? defaultFilter.value,
  );
  if (!props.groupByLane) {
    return entries;
  }
  const key = props.amountToSelectKey;
  const grouped = new Map<LedgerEntry['lane'], LedgerEntry>();
  for (const entry of entries) {
    const existing = grouped.get(entry.lane);
    if (!existing) {
      grouped.set(entry.lane, { ...entry });
      continue;
    }
    existing.amount += entry.amount;
    if (key !== 'amount') {
      existing[key] += entry[key];
    }
  }
  return Array.from(grouped.values());
});

const isSelected = (id: string) => props.modelValue.some((e) => e.id === id);

const getDefaultAmount = (entry: LedgerEntry) => entry[props.amountToSelectKey] as number;

const getCustomAmount = (entry: LedgerEntry) => {
  const selected = props.modelValue.find((e) => e.id === entry.id);
  const amountInCents = selected?.customAmount ?? getDefaultAmount(entry);
  return amountInCents / 100;
};

const getMinAmount = (entry: LedgerEntry) => {
  if (props.allowAllocationOutsideOfBounds) return undefined;
  const defaultAmount = getDefaultAmount(entry);
  return defaultAmount >= 0 ? 0 : defaultAmount / 100;
};

const getMaxAmount = (entry: LedgerEntry) => {
  if (props.allowAllocationOutsideOfBounds) return undefined;
  const defaultAmount = getDefaultAmount(entry);
  return defaultAmount >= 0 ? defaultAmount / 100 : 0;
};

const updateCustomAmount = (entry: LedgerEntry, value: string | number | null | undefined) => {
  const amountInCents =
    value === undefined || value === null
      ? 0
      : !Number.isNaN(Number(value))
        ? Math.round(Number(value) * 100)
        : getDefaultAmount(entry);
  const next = props.modelValue.map((e) => {
    if (e.id !== entry.id) return e;
    return { id: entry.id, customAmount: amountInCents };
  });
  emit('update:modelValue', next);
};

const selectedAmountToAllocateSum = computed(() => {
  return props.modelValue.reduce((sum, sel) => {
    const entry = ledgerEntries.value.find((e) => e.id === sel.id);
    if (!entry) return sum;
    const amount = sel.customAmount !== undefined ? sel.customAmount : getDefaultAmount(entry);
    return sum + amount;
  }, 0);
});

watch(
  [ledgerEntries, status],
  ([entries, queryStatus]) => {
    if (queryStatus !== 'success') return;

    const visibleIds = new Set(entries.map((entry) => entry.id));
    const filteredSelection = props.modelValue.filter((entry) => visibleIds.has(entry.id));
    if (filteredSelection.length !== props.modelValue.length) {
      emit('update:modelValue', filteredSelection);
    }
  },
  { immediate: true },
);

watch(
  selectedAmountToAllocateSum,
  (newVal) => {
    emit('updateTotalAmount', newVal);
  },
  { immediate: true },
);

const toggleEntry = (id: string) => {
  const isCurrentlySelected = isSelected(id);
  const canSetCustomAmount = can(
    'create',
    ['amountToPay', 'amountToRequestPay'].includes(props.amountToSelectKey)
      ? 'PaymentAllocation'
      : 'InvoiceAllocation',
    'amount',
  );
  const customAmount = canSetCustomAmount
    ? Math.round(getCustomAmount(ledgerEntries.value.find((e) => e.id === id)!) * 100)
    : undefined;

  if (isCurrentlySelected) {
    emit(
      'update:modelValue',
      props.modelValue.filter((e) => e.id !== id),
    );
  } else {
    emit('update:modelValue', [...props.modelValue, { id, customAmount }]);
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "description": "Description",
    "dueDate": "Due Date",
    "amountToPay": "To Pay",
    "amount": "Amount",
    "totalSelected": "Total selected",
    "noLedgerEntries": "No open ledger entries found",
    "allocate": "Allocate",
    "selectAtLeastOne": "Select at least one ledger entry",
    "open": "Open"
  },
  "de": {
    "description": "Beschreibung",
    "dueDate": "Fälligkeitsdatum",
    "amountToPay": "Zu zahlen",
    "amount": "Betrag",
    "totalSelected": "Summe ausgewählt",
    "noLedgerEntries": "Keine offenen Forderungseinträge gefunden",
    "allocate": "Zuordnen",
    "selectAtLeastOne": "Bitte mindestens einen Forderungseintrag auswählen",
    "open": "Offen"
  },
  "es": {
    "description": "Descripción",
    "dueDate": "Fecha de vencimiento",
    "amountToPay": "A pagar",
    "amount": "Importe",
    "totalSelected": "Total seleccionado",
    "noLedgerEntries": "No hay partidas abiertas",
    "allocate": "Imputar",
    "selectAtLeastOne": "Seleccione al menos una partida",
    "open": "Pendiente"
  }
}
</i18n>
